var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : new P(function (resolve) { resolve(result.value); }).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { ensureArray, isObject, hasKey } from './visio-core';
import { VisioPropertiesManager } from './visio-theme';
import { MinimalZipReader, xmlToJsObject } from './zipReader';
/**
 * Resolves a relationship Target into a canonical ZIP path.
 *
 * Normalizes slashes, trims leading slashes, and resolves "." and ".." segments
 * against the given base directory using a stack-based algorithm. Preserves paths
 * already under "visio/" as-is.
 *
 * This function is essential for resolving relative relationship targets in OPC
 * (Open Packaging Convention) packages, ensuring all paths are canonical and
 * normalized to use forward slashes.
 *
 * @param {string} baseDir - The base directory path for relative resolution (e.g., "visio/pages").
 *                           Should not have a trailing slash.
 * @param {string} target - The relationship target path to normalize (e.g., "../masters/master1.xml").
 *                          May be relative (../, ./), absolute under visio/, or a direct file reference.
 * @returns {string} The canonical ZIP path (e.g., "visio/masters/master1.xml").
 *
 * @example
 * normalizeZipPath("visio/pages", "../masters/master1.xml")
 * // Returns: "visio/masters/master1.xml"
 *
 * normalizeZipPath("visio/pages/_rels", "../page1.xml.rels")
 * // Returns: "visio/pages/page1.xml.rels"
 *
 * @private
 */
function normalizeZipPath(baseDir, target) {
    // ==================== Normalize Base Directory ====================
    // Remove backslashes, leading/trailing slashes to create clean base path
    const base = baseDir.replace(/\\/g, '/').replace(/^\/+/, '').replace(/\/+$/, '');
    // ==================== Normalize Target ====================
    // Remove backslashes and leading slashes from target
    const t = target.replace(/\\/g, '/').replace(/^\/+/, '');
    // ==================== Already Absolute ====================
    // If target already starts with "visio/", return as-is (absolute path in archive)
    if (/^visio\//i.test(t)) {
        return t;
    }
    // ==================== Build Stack for Path Resolution ====================
    // Split base into segments and filter empty strings
    const stack = base.split('/').filter(function (s) { return !!s; });
    // Split target into segments and filter empty strings
    const parts = t.split('/').filter(function (s) { return !!s; });
    // ==================== Process Path Segments ====================
    // Process each segment in target, handling . and .. navigation
    for (let i = 0; i < parts.length; i += 1) {
        const p = parts[parseInt(i.toString(), 10)];
        // Current directory reference - skip
        if (p === '.') {
            continue;
        }
        // Parent directory reference - pop from stack if available
        if (p === '..') {
            if (stack.length > 0) {
                stack.pop();
            }
        }
        // Regular segment - push onto stack
        else {
            stack.push(p);
        }
    }
    // ==================== Return Canonical Path ====================
    return stack.join('/');
}
/**
 * Returns the parent directory of a normalized ZIP path.
 *
 * Extracts the directory portion of a given path by finding the last forward slash.
 * Assumes input is already normalized (forward slashes, no trailing slash).
 *
 * @param {string} path - The normalized ZIP path (e.g., "visio/pages/page1.xml").
 *                        Should use forward slashes and not have trailing slash.
 * @returns {string} The parent directory path (e.g., "visio/pages").
 *                   Returns empty string if no directory separator found.
 *
 * @example
 * dirname("visio/pages/page1.xml") // Returns: "visio/pages"
 * dirname("visio/document.xml")    // Returns: "visio"
 * dirname("file.xml")              // Returns: ""
 *
 * @private
 */
function dirname(path) {
    // ==================== Normalize Path ====================
    // Convert backslashes to forward slashes and remove trailing slashes
    const norm = path.replace(/\\/g, '/').replace(/\/+$/, '');
    // ==================== Find Last Directory Separator ====================
    // Search for last forward slash to find directory boundary
    const idx = norm.lastIndexOf('/');
    // ==================== Return Result ====================
    // If no separator found, return empty string (root level)
    // Otherwise return everything up to (but not including) the last separator
    return idx === -1 ? '' : norm.slice(0, idx);
}
/**
 * Safely extracts the list of Page entries from a parsed pages.xml object.
 *
 * The parsed pages.xml may have different structures depending on the XML parser:
 * - Root structure: { Pages: { Page: [...] } }
 * - Flattened structure: { Page: [...] }
 * - Empty/missing: { }
 *
 * This function handles all variants and returns a consistent array.
 *
 * @param {ParsedXmlObject} pagesJsObj - The parsed pages.xml as a JavaScript object.
 *                                       Expected to have Pages element containing Page children.
 * @returns {VisioPageMetadata[]} An array of page metadata entries or empty array if none found.
 *                                Each entry represents a page index record with $ attributes.
 *
 * @example
 * const pages = getPagesArray(parsedPagesXml);
 * pages.forEach(page => {
 *     console.log(page.$.ID); // Page ID
 *     console.log(page.$.Name); // Page name
 * });
 *
 * @private
 */
function getPagesArray(pagesJsObj) {
    // ==================== Extract Pages Root ====================
    // Start with the object, then drill down to Pages if it exists
    let pagesRoot = pagesJsObj;
    if (hasKey(pagesJsObj, 'Pages')) {
        pagesRoot = pagesJsObj['Pages'];
    }
    // ==================== Extract Page Elements ====================
    // Look for Page element(s) within the root
    let pageNode = undefined;
    if (hasKey(pagesRoot, 'Page')) {
        pageNode = pagesRoot['Page'];
    }
    // ==================== Return Array ====================
    // ensureArray converts single items to [item] and handles undefined
    return ensureArray(pageNode ? pageNode : []);
}
/**
 * Safely extracts the list of Relationship entries from a parsed .rels XML file.
 *
 * The parsed .rels file may have different structures:
 * - Root structure: { Relationships: { Relationship: [...] } }
 * - Flattened structure: { Relationship: [...] }
 * - Empty/missing: { }
 *
 * This function handles all variants and returns a consistent array.
 *
 * @param {ParsedXmlObject} relsJsObj - The parsed .rels file as a JavaScript object.
 *                                      Expected to have Relationships element with Relationship children.
 * @returns {XmlRelationship[]} An array of relationship entries or empty array if none found.
 *                              Each entry has $ attributes with Id, Type, Target, etc.
 *
 * @example
 * const relationships = getRelationshipsArray(parsedRelsXml);
 * relationships.forEach(rel => {
 *     console.log(rel.$.Id);     // Relationship ID
 *     console.log(rel.$.Target); // Target file path
 * });
 *
 * @private
 */
function getRelationshipsArray(relsJsObj) {
    // ==================== Extract Relationships Root ====================
    // Start with the object, then drill down to Relationships if it exists
    let relsRoot = relsJsObj;
    if (hasKey(relsJsObj, 'Relationships')) {
        relsRoot = relsJsObj['Relationships'];
    }
    // ==================== Extract Relationship Elements ====================
    // Look for Relationship element(s) within the root
    let relNode = undefined;
    if (hasKey(relsRoot, 'Relationship')) {
        relNode = relsRoot['Relationship'];
    }
    // ==================== Return Array ====================
    // ensureArray converts single items to [item] and handles undefined
    return ensureArray(relNode ? relNode : []);
}
/**
 * Finds a ZIP entry by exact path name.
 *
 * Performs a linear search through the entries array to locate a matching path.
 * This is a simple utility for locating ZIP file entries by their exact names.
 *
 * @param {ZipEntry[]} entries - The array of available ZIP entries to search through.
 * @param {string} path - The exact path to search for (e.g., "visio/pages/pages.xml").
 *                        Must match exactly (case-sensitive).
 * @returns {ZipEntry | undefined} The matching ZipEntry if found, undefined otherwise.
 *
 * @example
 * const entry = findEntry(zipEntries, "visio/document.xml");
 * if (entry) {
 *     const data = await zip.extract(entry);
 * }
 *
 * @private
 */
function findEntry(entries, path) {
    // ==================== Linear Search ====================
    // Iterate through entries looking for exact path match
    for (let i = 0; i < entries.length; i += 1) {
        const e = entries[parseInt(i.toString(), 10)];
        if (e.name === path) {
            return e;
        }
    }
    // ==================== Not Found ====================
    return undefined;
}
/**
 * Traverses OPC (Open Packaging Convention) relationships and collects dependent parts.
 *
 * Performs a breadth-first search starting from a .rels file, with a maximum depth limit
 * to prevent runaway inclusion and infinite loops. This function only collects targets
 * under "visio/" directory and ignores external parts. For each discovered part, if a
 * sibling .rels file exists, it is queued for traversal.
 *
 * The traversal follows the OPC package model where relationships define dependencies
 * between parts, allowing discovery of all transitively required XML parts like themes,
 * masters, fonts, and data parts.
 *
 * @param {MinimalZipReader} zip - The ZIP reader instance for extracting and parsing .rels files.
 * @param {ZipEntry[]} visioEntries - All "visio/" entries available in the VSDX archive.
 *                                    Used to check for existence before adding to results.
 * @param {string} startRelsPath - The starting relationships part path to traverse from
 *                                (e.g., "visio/pages/_rels/page1.xml.rels").
 * @param {number} maxDepth - Maximum relationship nesting depth to traverse (e.g., 3).
 *                            Prevents infinite recursion and controls inclusion depth.
 * @param {ParsingContext} context - Parser context for collecting non-fatal warnings.
 *                                   Used to log parsing failures and missing relationships.
 * @returns {Promise<Set<string>>} A Promise resolving to a Set of canonical ZIP paths discovered
 *                                 during breadth-first traversal. Paths are normalized and
 *                                 only include entries that exist in visioEntries.
 *
 * @example
 * const deps = await collectDependencies(
 *     zip,
 *     visioEntries,
 *     "visio/pages/_rels/page1.xml.rels",
 *     3,
 *     context
 * );
 * // Returns Set containing paths like "visio/theme/theme1.xml", "visio/masters/master1.xml"
 *
 * @private
 */
function collectDependencies(zip, visioEntries, startRelsPath, maxDepth, context) {
    return __awaiter(this, void 0, void 0, function* () {
        // ==================== Initialize BFS Queue and Tracking ====================
        // Queue contains tuples of (relsPath, currentDepth) for breadth-first traversal
        const toVisit = [{ relsPath: startRelsPath, depth: 0 }];
        // Track which .rels files we've already processed to avoid cycles
        const visitedRels = new Set();
        // Accumulate discovered part paths
        const collected = new Set();
        // ==================== Breadth-First Traversal ====================
        while (toVisit.length > 0) {
            const item = toVisit.shift();
            if (!item) {
                break;
            }
            const relsPath = item.relsPath;
            const depth = item.depth;
            // ==================== Skip if Already Visited ====================
            // Prevent reprocessing the same .rels file
            if (visitedRels.has(relsPath)) {
                continue;
            }
            visitedRels.add(relsPath);
            // ==================== Extract and Parse .rels File ====================
            const relsEntry = findEntry(visioEntries, relsPath);
            if (!relsEntry) {
                continue;
            }
            let relsJs;
            try {
                // Extract binary data from ZIP
                const data = yield zip.extract(relsEntry);
                // Decode as UTF-8 text
                const text = new TextDecoder('utf-8').decode(data);
                // Parse XML using browser DOMParser
                const doc = new DOMParser().parseFromString(text, 'text/xml');
                // Convert DOM to JS object
                relsJs = xmlToJsObject(doc.documentElement);
            }
            catch (_e) {
                // Log non-fatal parsing error and continue with next item
                context.addWarning('[ERROR] :: Failed to parse relationships: ' + relsPath);
                continue;
            }
            // ==================== Extract Relationships ====================
            const rels = getRelationshipsArray(relsJs);
            // Get base directory for resolving relative paths
            // Remove the _rels directory to get the base (e.g., "visio/pages/_rels/..." -> "visio/pages")
            const baseDir = dirname(relsPath).replace(/\/_rels$/, '');
            // ==================== Process Each Relationship ====================
            for (let i = 0; i < rels.length; i += 1) {
                const rel = rels[parseInt(i.toString(), 10)];
                // Skip invalid relationships
                if (!rel || !rel.$ || typeof rel.$.Target !== 'string') {
                    continue;
                }
                // ==================== Normalize Target Path ====================
                // Resolve relative paths like "../theme/theme1.xml" to canonical paths
                const resolved = normalizeZipPath(baseDir, rel.$.Target);
                // ==================== Filter to Visio Parts Only ====================
                // Only include parts under visio/ directory (exclude external parts and not selected page)
                if (!/^visio\//i.test(resolved) || /(?:^|\/)page\d+\.xml$/i.test(resolved)) {
                    continue;
                }
                // ==================== Add to Collected ====================
                const partEntry = findEntry(visioEntries, resolved);
                if (partEntry) {
                    collected.add(resolved);
                    // ==================== Queue .rels Traversal ====================
                    // If depth limit not reached, traverse this part's .rels file if it exists
                    if (depth < maxDepth) {
                        // Split path to find parent directory and file name
                        const split = resolved.split('/');
                        const base = split.slice(0, split.length - 1).join('/');
                        const file = split[split.length - 1] || '';
                        // Construct path to .rels file for this part
                        const relsCandidate = base + '/_rels/' + file + '.rels';
                        // Queue for traversal if it exists
                        if (findEntry(visioEntries, relsCandidate)) {
                            toVisit.push({ relsPath: relsCandidate, depth: depth + 1 });
                        }
                    }
                }
            }
        }
        return collected;
    });
}
/**
 * Safely gets a string attribute from an object.
 *
 * @param {ParsedXmlObject | undefined} obj - The object to get attribute from.
 * @param {string} name - The attribute name.
 * @returns {string} The attribute value or empty string if not found.
 * @private
 */
function getAttr(obj, name) {
    if (obj && Object.prototype.hasOwnProperty.call(obj, name)) {
        const v = obj[`${name}`];
        if (typeof v === 'string') {
            return v;
        }
    }
    return '';
}
/**
 * Gets a string attribute and converts to lowercase.
 *
 * @param {object} obj - The object to get attribute from.
 * @param {string} name - The attribute name.
 * @returns {string} The lowercased attribute value or empty string.
 * @private
 */
function getAttrLower(obj, name) {
    const val = getAttr(obj, name);
    return val.toLowerCase();
}
/**
 * Determines if a page is a background page.
 *
 * Background pages are templates shared across multiple pages.
 * We only want to load non-background pages.
 *
 * @param {VisioPageMetadata | ParsedXmlObject} page - The page metadata to check.
 * @returns {boolean} True if page is a background page, false otherwise.
 * @private
 */
export function isBackground(page) {
    const attrs = page.$;
    const bgFlag = getAttrLower(attrs, 'Background');
    const bgPageFlag = getAttrLower(attrs, 'BackgroundPage');
    return bgFlag === '1' || bgFlag === 'true' || bgPageFlag === '1' || bgPageFlag === 'true';
}
/**
 * Reads and aggregates Visio XML parts from a VSDX (ZIP) package.
 *
 * This class is the main entry point for reading VSDX files. It:
 * - Resolves the requested non-background page via relationship ID
 * - Normalizes relationship target paths
 * - Follows .rels files to include dependent parts (themes, masters, media, etc.)
 * - Parses only XML parts and retains binary parts separately
 * - Designed for browser runtimes (uses TextDecoder and DOMParser)
 *
 * The VSDX format is based on OPC (Open Packaging Convention), which uses
 * relationships to define dependencies between parts. This reader traverses
 * those relationships to collect all required files.
 *
 * @private
 */
export class VisioPackageReader {
    constructor() {
        // Task #1004826: Force-include additional parts (e.g., background page contents).
        // Set keeps it deduped and fast to check.
        this.extraIncludeParts = new Set();
        // Tracks the immediate background page files for the currently selected page
        this.currentBackgroundFiles = [];
    }
    /**
     * Reads a VSDX ArrayBuffer and returns a consolidated VisioDocumentStructure for the chosen page.
     *
     * This is the main public entry point. It:
     * 1. Validates the VSDX file structure
     * 2. Identifies non-background pages from pages.xml
     * 3. Triggers an import event for page selection
     * 4. Resolves the selected page via relationships
     * 5. Delegates to buildVisioDocumentStructure for part collection and parsing
     *
     * Errors are logged as warnings in the context, and import events are triggered
     * to allow caller-side cancellation or page selection.
     *
     * @param {ArrayBuffer} arrayBuffer - The VSDX file content as an ArrayBuffer.
     *                                    Must be a valid ZIP archive with Visio structure.
     * @param {ParsingContext} [context] - Collector for non-fatal warnings and event dispatching.
     *                                     If not provided, warnings will be lost.
     * @param {number} [pageIndex=0] - Zero-based index of the non-background page to extract.
     *                                Defaults to 0; if out of range, selects the last available page.
     * @param {boolean} [retrieval=false] - The retrieval is used to know this operation runs for import or export
     *                                Defaults to false;
     * @returns {Promise<VisioDocumentStructure | null>} A Promise resolving to the consolidated document structure
     *                                                    or null if fatal errors occur.
     *
     * @example
     * const reader = new VisioPackageReader();
     * const vsdxBuffer = await fetch('diagram.vsdx').then(r => r.arrayBuffer());
     * const structure = await reader.readPackage(vsdxBuffer, context, 0);
     * if (structure) {
     *     console.log(structure.Page); // Page content
     *     console.log(structure.Master); // Master definitions
     * }
     *
     * @private
     */
    readPackage(arrayBuffer, context, pageIndex = 0, retrieval = false) {
        return __awaiter(this, void 0, void 0, function* () {
            this.extraIncludeParts = new Set();
            this.currentBackgroundFiles = [];
            // ==================== Define Helper Functions ====================
            /**
             * Extracts the relationship ID (r:id or rId) from a page metadata.
             *
             * The r:id attribute links the page index entry to its actual page file.
             *
             * @param {VisioPageMetadata} page - The page metadata.
             * @returns {string} The relationship ID or empty string if not found.
             */
            function getPageRelId(page) {
                if (page) {
                    const maybeRel = page.Rel;
                    if (maybeRel && maybeRel.$) {
                        const rid = getAttr(maybeRel.$, 'r:id');
                        if (rid) {
                            return rid;
                        }
                        const ridAlt = getAttr(maybeRel.$, 'rId'); // some parsers drop namespace colon
                        if (ridAlt) {
                            return ridAlt;
                        }
                    }
                }
                return '';
            }
            try {
                // ==================== Initialize ZIP Reader ====================
                const zip = new MinimalZipReader(arrayBuffer);
                const allEntries = zip.getEntries();
                // ==================== Validate ZIP Structure ====================
                if ((!allEntries || allEntries.length === 0) && !retrieval) {
                    context.addWarning('[ERROR] :: VSDX file is empty or corrupted.');
                    context.triggerEvent('Import', { status: 'failed' });
                    return null;
                }
                // ==================== Filter to Visio Entries ====================
                // Only include entries under visio/ directory (exclude root-level OPC metadata)
                const visioEntries = allEntries.filter(function (entry) {
                    return entry.name.startsWith('visio/') && !entry.name.endsWith('/');
                });
                if (visioEntries.length === 0 && !retrieval) {
                    context.addWarning('[ERROR] :: Visio folder not found in VSDX file.');
                    context.triggerEvent('Import', { status: 'failed' });
                    return null;
                }
                // ==================== Extract Core Files ====================
                // pages.xml lists all pages; pages.xml.rels maps pages to their content files
                const pagesXmlEntry = findEntry(visioEntries, 'visio/pages/pages.xml');
                const pagesRelsEntry = findEntry(visioEntries, 'visio/pages/_rels/pages.xml.rels');
                if ((!pagesXmlEntry || !pagesRelsEntry) && !retrieval) {
                    context.addWarning('[ERROR] :: Core files pages.xml or pages.xml.rels not found.');
                    context.triggerEvent('Import', { status: 'failed' });
                    return null;
                }
                // ==================== Parse pages.xml and pages.xml.rels ====================
                const pagesJsObj = yield this.extractAndParseXml(zip, pagesXmlEntry);
                const relsJsObj = yield this.extractAndParseXml(zip, pagesRelsEntry);
                // ==================== Extract Non-Background Pages ====================
                const pageMetas = getPagesArray(pagesJsObj);
                const nonBgPages = [];
                const pagesForSelection = [];
                // Filter to non-background pages and prepare UI selection list
                for (let i = 0; i < pageMetas.length; i += 1) {
                    const p = pageMetas[parseInt(i.toString(), 10)];
                    if (!p || !p.$) {
                        continue;
                    }
                    if (isBackground(p)) {
                        continue;
                    }
                    nonBgPages.push(p);
                    // Build human-readable label for page
                    const attrs = p.$;
                    const idStr = typeof attrs['ID'] === 'string' ? String(attrs['ID']) :
                        typeof attrs['Id'] === 'string' ? String(attrs['Id']) : '';
                    const nameStr = typeof attrs['Name'] === 'string' ? String(attrs['Name']) :
                        typeof attrs['NameU'] === 'string' ? String(attrs['NameU']) : '';
                    const label = (nameStr && nameStr.trim().length > 0) ? nameStr : (idStr || '');
                    if (label) {
                        pagesForSelection.push({ name: label });
                    }
                    else {
                        pagesForSelection.push({ name: 'Page ' + String(pagesForSelection.length + 1) });
                    }
                }
                // Task #1004826-EXT: Fallback if no foreground (non-background) pages exist.
                // Instead of failing, pick the first background page so background-only files render.
                let selectedByFallbackBg = null;
                if (nonBgPages.length === 0) {
                    const firstBackground = (pageMetas || [])
                        .find((p) => p && p.$ && isBackground(p));
                    if (firstBackground) {
                        selectedByFallbackBg = firstBackground;
                        // Make the fallback visible to downstream selection logic by seeding nonBgPages
                        // (so code that indexes into nonBgPages still works without special cases).
                        nonBgPages.push(firstBackground);
                        // Also add a UI item label for consistency (optional; keep if your UI uses pagesForSelection)
                        const attrs = firstBackground.$;
                        let displayName;
                        if (attrs && typeof attrs['Name'] === 'string') {
                            displayName = attrs['Name'];
                        }
                        else if (attrs && typeof attrs['NameU'] === 'string') {
                            displayName = attrs['NameU'];
                        }
                        else {
                            const count = (pagesForSelection && pagesForSelection.length) ? pagesForSelection.length : 0;
                            displayName = 'Page ' + String(count + 1);
                        }
                        pagesForSelection.push({ name: displayName });
                    }
                    else {
                        // Truly no pages in the file
                        if (!retrieval) {
                            context.addWarning('[ERROR] :: No valid Visio pages found.');
                            context.triggerEvent('Import', { status: 'failed' });
                        }
                        return null;
                    }
                }
                // ==================== Validate and Clamp Page Index ====================
                let idx = Number.isInteger(pageIndex) ? pageIndex : parseInt(String(pageIndex), 10);
                if (!Number.isFinite(idx) || idx < 0) {
                    idx = 0;
                }
                if (nonBgPages.length > 0 && idx >= nonBgPages.length) {
                    idx = nonBgPages.length - 1;
                }
                // ==================== Trigger Import Event (Page Selection) ====================
                // Allows caller to select page or cancel import
                const args = {
                    status: 'started',
                    pages: pagesForSelection,
                    selectedPage: { name: pagesForSelection[parseInt(idx.toString(), 10)].name }
                };
                if (!retrieval) {
                    context.triggerEvent('Import', args);
                    // Check for cancellation
                    if (args.cancel) {
                        return null;
                    }
                }
                // ==================== Resolve Selected Page Index ====================
                let selectedPageIndex = pagesForSelection.findIndex((p) => p.name === args.selectedPage.name);
                selectedPageIndex = selectedPageIndex < 0 ? 0 : selectedPageIndex;
                // Task #1004826-EXT: Prefer UI-selected foreground; otherwise use the background fallback.
                const selectedPage = selectedByFallbackBg || nonBgPages[parseInt(selectedPageIndex.toString(), 10)];
                if ((!selectedPage || !selectedPage.$) && !retrieval) {
                    context.addWarning('[ERROR] :: Selected page metadata missing.');
                    context.triggerEvent('Import', { status: 'failed' });
                    return null;
                }
                // ==================== Build Relationship ID Map ====================
                // Create map from relationship $.Id to relationship for quick lookup
                const relationships = getRelationshipsArray(relsJsObj);
                const idToRel = new Map();
                for (let i = 0; i < relationships.length; i += 1) {
                    const r = relationships[parseInt(i.toString(), 10)];
                    if (!r || !r.$) {
                        continue;
                    }
                    const relObj = r.$;
                    const relId = relObj.Id;
                    if (typeof relId === 'string' && relId.length > 0) {
                        idToRel.set(relId, r);
                    }
                }
                let targetPageRel = undefined;
                // ==================== Resolve Target Page Relationship ====================
                // Primary: use page's r:id to lookup relationship
                const pageRelId = getPageRelId(selectedPage);
                if (pageRelId && idToRel.has(pageRelId)) {
                    const rel = idToRel.get(pageRelId);
                    if (rel) {
                        targetPageRel = rel;
                    }
                }
                // ==================== Fallback: Align by Order ====================
                // If primary lookup failed, try aligning by order of non-background pages
                const orderedNonBgRels = [];
                for (let i = 0; i < nonBgPages.length; i += 1) {
                    const p = nonBgPages[parseInt(i.toString(), 10)];
                    const rid = getPageRelId(p);
                    if (rid && idToRel.has(rid)) {
                        const rel = idToRel.get(rid);
                        if (rel) {
                            orderedNonBgRels.push(rel);
                        }
                    }
                }
                if (selectedPageIndex >= 0 && selectedPageIndex < orderedNonBgRels.length) {
                    targetPageRel = orderedNonBgRels[parseInt(selectedPageIndex.toString(), 10)];
                }
                if (!targetPageRel && !retrieval) {
                    context.addWarning('[ERROR] :: Could not resolve page relationship. No valid page found.');
                    context.triggerEvent('Import', { status: 'failed' });
                    return null;
                }
                // Task #1004826: If the selected page references a background (BackPage),
                // force-include that background PageContents so its "Solid" (background color) is parsed.
                const backPageId = selectedPage.$ && selectedPage.$.BackPage;
                if (backPageId) {
                    // Find the background page metadata by ID in pages.xml
                    const backgroundPageMeta = (pageMetas || []).find((p) => p && p.$ && String(p.$.ID) === String(backPageId));
                    if (backgroundPageMeta) {
                        // Resolve r:id of the background page → actual PageContents (e.g., visio/pages/page1.xml)
                        const backgroundRelId = getPageRelId(backgroundPageMeta);
                        if (backgroundRelId && idToRel.has(backgroundRelId)) {
                            const bgRel = idToRel.get(backgroundRelId);
                            if (bgRel && bgRel.$ && typeof bgRel.$.Target === 'string') {
                                // Canonical ZIP path for pageN.xml
                                const bgPageFilePath = normalizeZipPath('visio/pages', bgRel.$.Target);
                                this.extraIncludeParts.add(bgPageFilePath);
                                // Track the immediate background page file of the selected page
                                if (typeof bgPageFilePath === 'string' && bgPageFilePath.length > 0) {
                                    if (this.currentBackgroundFiles.indexOf(bgPageFilePath) === -1) {
                                        this.currentBackgroundFiles.push(bgPageFilePath);
                                    }
                                }
                                // Also include its .rels if it exists (lets dependency walk pick up media, etc.)
                                const parts = bgPageFilePath.split('/');
                                const bgFileName = parts[parts.length - 1] || '';
                                const bgRelsPath = normalizeZipPath('visio/pages/_rels', bgFileName + '.rels');
                                if (findEntry(visioEntries, bgRelsPath)) {
                                    this.extraIncludeParts.add(bgRelsPath);
                                }
                            }
                        }
                    }
                }
                // ==================== Build Document Structure ====================
                return this.buildVisioDocumentStructure(zip, visioEntries, targetPageRel, context, retrieval);
            }
            catch (error) {
                // ==================== Handle Fatal Errors ====================
                const errorMessage = error instanceof Error ? error.message : 'Unknown error';
                if (!retrieval) {
                    context.addWarning('[ERROR] :: Package reader error: ' + errorMessage);
                    context.triggerEvent('Import', { status: 'failed' });
                }
                return null;
            }
        });
    }
    /**
     * Builds the VisioDocumentStructure for the selected page.
     *
     * This method:
     * 1. Collects base entries (page, masters, document.xml, etc.)
     * 2. Traverses .rels files to discover dependencies (themes, fonts, media, etc.)
     * 3. Parses XML entries and extracts binary parts
     * 4. Merges parsed XML into typed document fields
     * 5. Stores unmapped data in __Extensions bag for extensibility
     * 6. Preserves binary data in __BinaryParts bag
     *
     * This structure is then ready for parsing by the VisioImporter.
     *
     * @param {MinimalZipReader} zip - The ZIP reader instance for extracting entries.
     * @param {ZipEntry[]} visioEntries - All "visio/" entries available in the VSDX archive.
     * @param {XmlRelationship} targetPageRel - The resolved relationship entry pointing to the target page.
     *                                          Must have $.Id, $.Type, and $.Target properties.
     * @param {ParsingContext} context - Collector array for non-fatal warnings during processing.
     * @param {boolean} [retrieval] - The retrieval is used to know this operation runs for import or export
     *
     * @returns {Promise<VisioDocumentStructure>} A Promise resolving to the complete VisioDocumentStructure.
     *
     * @private
     */
    buildVisioDocumentStructure(zip, visioEntries, targetPageRel, context, retrieval) {
        return __awaiter(this, void 0, void 0, function* () {
            const visioDoc = {};
            visioDoc.PageRelId = targetPageRel.$.Id;
            // ==================== Resolve Target Page File ====================
            // Normalize the relationship target path to a canonical ZIP path
            const targetPageFileName = normalizeZipPath('visio/pages', targetPageRel.$.Target);
            // ==================== Get Initial Entries ====================
            // Includes: target page, document.xml, settings, windows, masters, themes
            let initialEntries = visioEntries;
            if (!retrieval) {
                initialEntries = this.filterEntriesToExtract(visioEntries, targetPageFileName);
            }
            // Task #1004826: Merge any forced-included parts (e.g., background page1.xml)
            // into the initial set before dependency traversal, with simple dedupe.
            if (this.extraIncludeParts && this.extraIncludeParts.size > 0) {
                const already = new Set(initialEntries.map((e) => e.name));
                this.extraIncludeParts.forEach((zipPath) => {
                    const entry = findEntry(visioEntries, zipPath);
                    if (entry && !already.has(entry.name)) {
                        initialEntries.push(entry);
                        already.add(entry.name);
                    }
                });
            }
            // ==================== Resolve Target Page .rels Path ====================
            // Construct the path to the .rels file for this page
            const split = targetPageFileName.split('/');
            const targetPageName = split.length > 0 ? split[split.length - 1] : '';
            const targetPageRelsPath = normalizeZipPath('visio/pages/_rels', targetPageName + '.rels');
            // ==================== Collect Transitive Dependencies ====================
            const extraPaths = new Set();
            // ==================== Traverse Target Page .rels ====================
            // Discover parts referenced by the page (media, shapes, themes, etc.)
            const pageRelsExists = !!findEntry(visioEntries, targetPageRelsPath);
            if (pageRelsExists) {
                try {
                    const deps = yield collectDependencies(zip, visioEntries, targetPageRelsPath, 3, context);
                    deps.forEach(function (p) { extraPaths.add(p); });
                }
                catch (_err) {
                    context.addWarning('[ERROR] :: Failed to traverse page relationships for dependency inclusion.');
                }
            }
            // ==================== Traverse Document.xml.rels ====================
            // Discover document-level parts (themes, fonts, data parts, etc.)
            const documentRelsPath = 'visio/_rels/document.xml.rels';
            if (findEntry(visioEntries, documentRelsPath)) {
                try {
                    const docDeps = yield collectDependencies(zip, visioEntries, documentRelsPath, 3, context);
                    docDeps.forEach(function (p) {
                        // ==================== Filter Out Unrelated Pages ====================
                        // Avoid bringing in other pages than the selected one
                        if ((/^visio\/pages\/page\d+\.xml$/i.test(p) || /^visio\/pages\/_rels\/page\d+\.xml\.rels$/i.test(p)) && p !== targetPageFileName && p !== targetPageRelsPath) {
                            return;
                        }
                        extraPaths.add(p);
                    });
                }
                catch (_err) {
                    context.addWarning('[ERROR] :: Failed to traverse document relationships for dependency inclusion.');
                }
            }
            // ==================== Merge Initial + Dependencies ====================
            // Build final entry list with deduplication
            const nameSet = new Set();
            for (let i = 0; i < initialEntries.length; i += 1) {
                nameSet.add(initialEntries[parseInt(i.toString(), 10)].name);
            }
            const finalEntries = initialEntries.slice();
            extraPaths.forEach(function (p) {
                const entry = findEntry(visioEntries, p);
                if (entry && !nameSet.has(entry.name)) {
                    finalEntries.push(entry);
                    nameSet.add(entry.name);
                }
            });
            // ==================== Separate XML from Binary ====================
            // Determines if a file path appears to be XML-like.
            function isXmlLikePath(name) {
                return (/\.xml$/i.test(name) || /\.rels$/i.test(name));
            }
            // Returns priority for sorting entries by processing order.
            // Lower numbers are processed first (pages.xml before pages, etc.).
            function getFilePriority(fileName) {
                if (fileName === 'visio/pages/pages.xml') {
                    return 1;
                }
                if (fileName === 'visio/masters/masters.xml') {
                    return 2;
                }
                if (fileName.startsWith('visio/masters/master')) {
                    return 3;
                }
                if (fileName.startsWith('visio/pages/page')) {
                    return 4;
                }
                return 5;
            }
            // ==================== Filter XML vs Binary Entries ====================
            // Only parse XML-like entries; avoid parsing media/binary parts directly
            const xmlEntries = finalEntries.filter(function (e) { return isXmlLikePath(e.name); });
            const binEntries = finalEntries.filter(function (e) { return !isXmlLikePath(e.name); });
            // ==================== Sort Entries ====================
            // Sort for correct processing order (pages before shapes, etc.)
            xmlEntries.sort((a, b) => {
                const priorityA = getFilePriority(a.name);
                const priorityB = getFilePriority(b.name);
                if (priorityA !== priorityB) {
                    return priorityA - priorityB;
                }
                return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' });
            });
            // ==================== Extract and Parse XML Entries ====================
            // Parallel extraction and parsing for performance
            const parsed = yield Promise.all(xmlEntries.map((e) => this.extractAndParseXml(zip, e)));
            for (let i = 0; i < xmlEntries.length; i += 1) {
                this.mergeIntoDocument(visioDoc, xmlEntries[parseInt(i.toString(), 10)].name, parsed[parseInt(i.toString(), 10)]);
            }
            // Initialize from Visio document
            const propertyManager = new VisioPropertiesManager();
            this.validateDocumentStructure(visioDoc);
            propertyManager.initialize(visioDoc.RootDocument);
            context.propertyManager = propertyManager;
            // ==================== Extract and Store Binary Parts ====================
            // Store binary parts to avoid data loss
            if (binEntries.length > 0) {
                const binBag = this.getOrInitBinaryParts(visioDoc);
                yield Promise.all(binEntries.map((e) => __awaiter(this, void 0, void 0, function* () {
                    try {
                        const data = yield zip.extract(e);
                        binBag[e.name] = data; // Uint8Array
                    }
                    catch (_err) {
                        context.addWarning('[ERROR] :: Failed to extract binary part: ' + e.name);
                    }
                })));
            }
            // Remember selected page file for downstream importer
            visioDoc.__SelectedPageFile = targetPageFileName;
            // Remember the immediate background page files for the selected page
            visioDoc.__BackgroundPageFiles = this.currentBackgroundFiles.slice();
            // Build normalized indexes for masters and per-page connects
            this.buildNormalizedIndexes(visioDoc, context);
            // Expose normalized bundle to the parsing pipeline via context
            context.data.normalized = {
                parts: this.getOrInitParts(visioDoc),
                index: visioDoc.__Index,
                selectedPageFile: visioDoc.__SelectedPageFile,
                backgroundPageFiles: visioDoc.__BackgroundPageFiles
            };
            return visioDoc;
        });
    }
    /**
     * Validates that the document structure is safe to process.
     * @throws {Error} If document structure is invalid or suspicious
     *
     * @param {VisioDocumentStructure} visioDoc - Contains parsed elements from the XML
     *
     * @returns {void}
     *
     */
    validateDocumentStructure(visioDoc) {
        if (!visioDoc) {
            throw new Error('Document is null or undefined');
        }
        if (typeof visioDoc !== 'object') {
            throw new Error('Document must be an object');
        }
        if (!visioDoc.RootDocument) {
            throw new Error('Missing RootDocument in document structure');
        }
        // Add more validation as needed
    }
    /**
     * Returns the minimal set of entries required before dependency traversal.
     *
     * Always includes:
     * - Target page XML and its .rels file
     *
     * Document-level includes:
     * - document.xml, settings.xml, windows.xml (core document structure)
     *
     * Includes all:
     * - visio/masters/ (all master definitions)
     * - visio/theme/ (all theme definitions)
     *
     * Excludes:
     * - Other pages and their .rels files (to avoid unnecessary parsing)
     *
     * This base set is then supplemented by collectDependencies for a complete picture.
     *
     * @param {ZipEntry[]} visioEntries - All "visio/" entries available in the VSDX archive.
     * @param {string} targetPageFileName - The resolved file name of the target page
     *                                      (e.g., "visio/pages/page1.xml").
     * @returns {ZipEntry[]} An array of filtered ZipEntry objects ready for extraction.
     *
     * @private
     */
    filterEntriesToExtract(visioEntries, targetPageFileName) {
        const entries = [];
        // ==================== Calculate Target Page .rels Path ====================
        const split = targetPageFileName.split('/');
        const targetPageName = split.length > 0 ? split[split.length - 1] : '';
        const targetPageRelsName = normalizeZipPath('visio/pages/_rels', targetPageName + '.rels');
        // ==================== Filter Entries ====================
        for (let i = 0; i < visioEntries.length; i += 1) {
            const entry = visioEntries[parseInt(i.toString(), 10)];
            const name = entry.name;
            // ==================== Always Include Target Page ====================
            // Include target page and its relationships file
            if (name === targetPageFileName || name === targetPageRelsName) {
                entries.push(entry);
                continue;
            }
            // ==================== Exclude Other Pages ====================
            // Skip other page files and their .rels to reduce parsing load
            if (/^visio\/pages\/page\d+\.xml$/i.test(name) || /^visio\/pages\/_rels\/page\d+\.xml\.rels$/i.test(name)) {
                continue;
            }
            // ==================== Include Document-Level Essentials ====================
            // Include core document structure files
            if (name === 'visio/document.xml' ||
                name === 'visio/settings.xml' ||
                name === 'visio/windows.xml' ||
                name.startsWith('visio/masters/') || // All masters
                name.startsWith('visio/theme/') || // All themes
                name === 'visio/masters/_rels/masters.xml.rels') {
                entries.push(entry);
            }
        }
        return entries;
    }
    /**
     * Merges a parsed XML part into the VisioDocumentStructure.
     *
     * Known files are mapped to typed fields:
     * - visio/document.xml → root properties (merged)
     * - visio/settings.xml → DocumentSettings
     * - visio/windows.xml  → Window[]
     * - visio/pages/pageN.xml → Page[]
     * - visio/masters/masterN.xml → Master[]
     * - Theme XML → a:themeElements
     * - Connections XML → Connects
     * - Shapes XML → Shapes
     *
     * Unknown XML parts are stored under __Extensions[fileName] to preserve data
     * and support future extensions.
     *
     * @param {VisioDocumentStructure} visioDoc - The document structure to merge into.
     *                                            Will be modified in place.
     * @param {string} fileName - The ZIP path of the file being merged
     *                           (e.g., "visio/document.xml").
     * @param {ParsedXmlObject} jsObj - The parsed XML content as a JavaScript object.
     *                                  Root element and its children.
     * @returns {void} - Modifies visioDoc in place.
     *
     * @private
     */
    mergeIntoDocument(visioDoc, fileName, jsObj) {
        // Always stash full parsed XML by file for normalized access later
        const partsBag = this.getOrInitParts(visioDoc);
        partsBag[fileName] = jsObj;
        // ==================== Windows ====================
        // Root element contains Window elements
        if (jsObj.Window) {
            this.appendToProperty(visioDoc, 'Window', jsObj);
        }
        // ==================== Pages ====================
        // Page elements from pages.xml
        if (jsObj.Page) {
            this.appendToProperty(visioDoc, 'Page', jsObj.Page);
        }
        // ==================== DocumentSettings ====================
        // Document-level settings from settings.xml
        if (jsObj.DocumentSettings) {
            this.appendToProperty(visioDoc, 'DocumentSettings', jsObj.DocumentSettings);
            this.appendToProperty(visioDoc, 'RootDocument', jsObj);
        }
        // ==================== Masters ====================
        // Master definitions from masters.xml and individual master files
        if (jsObj.Master) {
            this.appendToProperty(visioDoc, 'Master', jsObj.Master);
        }
        // ==================== Connections ====================
        // Connection records from page content
        if (jsObj.Connects && Object.keys(jsObj.Connects).length > 0) {
            this.appendToProperty(visioDoc, 'Connects', jsObj.Connects);
        }
        // ==================== Shapes ====================
        // Shape elements from page content
        if (jsObj.Shapes) {
            this.appendToProperty(visioDoc, 'Shapes', jsObj.Shapes);
        }
        // ==================== Theme Elements ====================
        // Theme color and font definitions
        if (jsObj['a:themeElements']) {
            this.appendToProperty(visioDoc, 'a:themeElements', jsObj['a:themeElements']);
        }
        // ==================== Relationships ====================
        // Raw relationship elements (for extensibility)
        if (jsObj.Relationship) {
            this.appendToProperty(visioDoc, 'Relationship', jsObj.Relationship);
        }
        // ==================== Stash Unknown Parts ====================
        // Store unmapped XML in extensions bag for future extensibility
        const bag = this.getOrInitExtensions(visioDoc);
        bag[`${fileName}`] = jsObj;
    }
    /**
     * Extracts a ZIP entry and converts its XML content to a JavaScript object.
     *
     * Uses browser APIs (TextDecoder and DOMParser) for extraction and parsing.
     * Detects and throws on invalid XML (parsererror elements).
     *
     * This is an async operation because ZIP extraction may involve I/O.
     *
     * @param {MinimalZipReader} zip - The ZIP reader instance for extracting.
     * @param {ZipEntry} entry - The ZIP entry to extract and parse.
     * @returns {Promise<ParsedXmlObject>} A Promise resolving to the ParsedXmlObject
     *                                     representing the root element.
     * @throws {Error} If XML parsing fails or the entry cannot be extracted.
     *
     * @private
     */
    extractAndParseXml(zip, entry) {
        return __awaiter(this, void 0, void 0, function* () {
            // ==================== Extract Binary Data ====================
            const data = yield zip.extract(entry);
            // ==================== Decode UTF-8 ====================
            const text = new TextDecoder('utf-8').decode(data);
            // ==================== Parse XML ====================
            const doc = new DOMParser().parseFromString(text, 'text/xml');
            const root = doc.documentElement;
            // ==================== Detect Parse Errors ====================
            // DOMParser doesn't always throw on invalid XML, so check for parsererror element
            if (!root || root.nodeName.toLowerCase() === 'parsererror') {
                throw new Error('XML parse error in ' + entry.name);
            }
            // ==================== Convert to JS Object ====================
            // For theme files, track element order (shouldTrackOrder = true)
            const shouldTrackOrder = entry.name.startsWith('visio/theme');
            return xmlToJsObject(root, shouldTrackOrder);
        });
    }
    /**
     * Type-safe helper to append an item to a property that can be a single item or an array.
     *
     * Handles promotion of singleton values to arrays as needed:
     * - If property is undefined/null: set to item
     * - If property is already an array: push item
     * - Otherwise: create array [existing, item]
     *
     * This allows flexible handling of both single and multiple elements
     * in the document structure.
     *
     * @template K - The key of the property in VisioDocumentStructure.
     * @template T - The type of the item being appended.
     * @param {VisioDocumentStructure} doc - The document object to modify (in place).
     * @param {K} key - The key of the property to append to.
     * @param {T} item - The item to append.
     * @returns {void} - Modifies doc in place.
     *
     * @example
     * appendToProperty(doc, 'Page', pageObj);
     * // doc.Page is now [pageObj] or [existing, pageObj]
     *
     * @private
     */
    appendToProperty(doc, key, item) {
        const existing = doc[`${key}`];
        // ==================== Case 1: Property is Empty ====================
        // First item being added
        if (existing === undefined || existing === null) {
            doc[`${key}`] = item;
            return;
        }
        // ==================== Case 2: Property is Already an Array ====================
        // Append to existing array
        if (Array.isArray(existing)) {
            existing.push(item);
            return;
        }
        // ==================== Case 3: Property is a Singleton ====================
        // Promote to array and add item
        doc[`${key}`] = [existing, item];
    }
    /**
     * Returns the __Extensions bag on the document, creating it if necessary.
     *
     * Used to store unmapped XML parts keyed by their ZIP path. This bag preserves
     * unknown or extension XML content that does not fit standard Visio structure fields.
     * This provides extensibility for future Visio features or custom XML.
     *
     * @param {VisioDocumentStructure} doc - The document structure to access or initialize.
     * @returns {Record<string, ParsedXmlObject>} The __Extensions bag as a key-value object
     *                                            mapping file paths to parsed XML objects.
     *
     * @example
     * const ext = getOrInitExtensions(doc);
     * ext['visio/custom/custom.xml'] = customXmlObj;
     *
     * @note Values are raw ParsedXmlObject instances; no schema validation is performed.
     *
     * @private
     */
    getOrInitExtensions(doc) {
        const key = '__Extensions';
        const current = doc[`${key}`];
        // ==================== Return if Already Initialized ====================
        if (isObject(current)) {
            return current;
        }
        // ==================== Create New Extensions Bag ====================
        const bag = {};
        doc[key] = bag;
        return bag;
    }
    /**
     * Returns the __BinaryParts bag on the document, creating it if necessary.
     *
     * Used to store non-XML assets (e.g., images, media files) keyed by their ZIP path.
     * This bag preserves binary data that cannot be represented as XML and allows
     * later retrieval by the import process.
     *
     * @param {VisioDocumentStructure} doc - The document structure to access or initialize.
     * @returns {Record<string, Uint8Array>} The __BinaryParts bag as a key-value object
     *                                       mapping file paths to Uint8Array buffers.
     *
     * @example
     * const bin = getOrInitBinaryParts(doc);
     * // Later: const imageData = bin['visio/media/image1.png'];
     *
     * @note This uses a loose object check; it does not validate that values are Uint8Array.
     *       Callers must only assign Uint8Array values to this bag.
     *
     * @private
     */
    getOrInitBinaryParts(doc) {
        const key = '__BinaryParts';
        const current = doc[`${key}`];
        // ==================== Return if Already Initialized ====================
        if (isObject(current)) {
            return current;
        }
        // ==================== Create New Binary Parts Bag ====================
        const bag = {};
        doc[key] = bag;
        return bag;
    }
    /**
     * Retrieves or initializes the internal `__Parts` bag on a Visio document structure.
     * If the `__Parts` property already exists and is an object, it is returned.
     * Otherwise, a new empty record is created, attached to the document object,
     * and then returned.
     *
     * @param {VisioDocumentStructure} doc - The Visio document structure to inspect or extend
     * @returns {Record<string, ParsedXmlObject>} A record mapping part names to parsed XML objects
     */
    getOrInitParts(doc) {
        const key = '__Parts';
        const docObj = doc;
        if (Object.prototype.hasOwnProperty.call(docObj, key)) {
            const descriptor = Object.getOwnPropertyDescriptor(docObj, key);
            const current = descriptor ? descriptor.value : undefined;
            if (current && typeof current === 'object') {
                return current;
            }
        }
        const bag = {};
        Object.defineProperty(docObj, key, { value: bag, writable: true, enumerable: true, configurable: true });
        return bag;
    }
    /**
     * Retrieves or initializes the internal `__Index` structure on a Visio document.
     * If the `__Index` property already exists and is an object, it is returned.
     * Otherwise, a new `NormalizedIndex` is created, attached to the document object,
     * and then returned.
     *
     * The `NormalizedIndex` contains:
     * - `mastersById`: Map of master IDs to master info
     * - `masterFileByRid`: Map of relationship IDs to master file names
     * - `masterChildByMasterId`: Map of master IDs to child shape maps
     * - `masterParentByMasterId`: Map of master IDs to parent-child relationships
     * - `masterImmediateChildrenByMasterId`: Map of master IDs to immediate child IDs
     * - `masterRootIdsByMasterId`: Map of master IDs to top-level shape IDs
     * - `pageConnectsByFile`: Map of page connections by file
     *
     * @param {VisioDocumentStructure} doc - The Visio document structure to inspect or extend
     * @returns {NormalizedIndex} A normalized index object for master and page references
     */
    getOrInitIndex(doc) {
        const key = '__Index';
        const docObj = doc;
        if (Object.prototype.hasOwnProperty.call(docObj, key)) {
            const descriptor = Object.getOwnPropertyDescriptor(docObj, key);
            const current = descriptor ? descriptor.value : undefined;
            if (current && typeof current === 'object') {
                return current;
            }
        }
        const indexObj = {
            mastersById: new Map(),
            masterFileByRid: new Map(),
            masterChildByMasterId: new Map(),
            masterParentByMasterId: new Map(),
            masterImmediateChildrenByMasterId: new Map(),
            masterRootIdsByMasterId: new Map(),
            pageConnectsByFile: new Map()
        };
        Object.defineProperty(docObj, key, { value: indexObj, writable: true, enumerable: true, configurable: true });
        return indexObj;
    }
    /**
     * Builds (or refreshes) the normalized `__Index` caches used by the importer.
     *
     * Populates master and page lookup maps:
     * - `masterFileByRid`: relationship Id -> master file path (from masters.xml.rels)
     * - `mastersById`: masterId -> { id, nameU, rid, fileName } (from masters.xml)
     * - `masterChildByMasterId`: masterId -> Map(shapeId -> shape node) for each master file
     * - `masterParentByMasterId`: masterId -> Map(childId -> parentId)
     * - `masterImmediateChildrenByMasterId`: masterId -> Map(parentId -> childId[])
     * - `masterRootIdsByMasterId`: masterId -> root shapeId[]
     * - `pageConnectsByFile`: page file -> Connects block (selected page only)
     *
     * Adds warnings to the parsing context when required master parts are missing.
     *
     * @param {VisioDocumentStructure} doc - Parsed document structure containing extracted parts and index storage.
     * @param {ParsingContext} context - Parsing context used to record warnings and shared state.
     * @returns {void}
     */
    buildNormalizedIndexes(doc, context) {
        const indexObj = this.getOrInitIndex(doc);
        const parts = this.getOrInitParts(doc);
        // ---- master rid -> file mapping from masters.xml.rels ----
        const mastersRelsKey = 'visio/masters/_rels/masters.xml.rels';
        const mastersRelsDescriptor = Object.getOwnPropertyDescriptor(parts, mastersRelsKey);
        const mastersRels = Object.prototype.hasOwnProperty.call(parts, mastersRelsKey)
            ? (mastersRelsDescriptor ? mastersRelsDescriptor.value : undefined)
            : undefined;
        if (mastersRels) {
            const relsArr = getRelationshipsArray(mastersRels);
            for (let i = 0; i < relsArr.length; i++) {
                const rel = relsArr[parseInt(i.toString(), 10)];
                if (rel && rel.$ && typeof rel.$.Id === 'string' && typeof rel.$.Target === 'string') {
                    const rid = rel.$.Id;
                    const file = normalizeZipPath('visio/masters', rel.$.Target);
                    // Map the relationship id to its master XML file when the file path points to a valid Visio master.
                    if (file.indexOf('visio/masters/master') === 0 && file.lastIndexOf('.xml') === file.length - 4) {
                        indexObj.masterFileByRid.set(rid, file);
                    }
                }
            }
        }
        else {
            context.addWarning('[WARNING] :: masters.xml.rels is missing; master mapping may be incomplete.');
        }
        // ---- masterId -> { id, nameU, rid, fileName } from masters.xml ----
        const mastersXmlKey = 'visio/masters/masters.xml';
        const mastersXmlDescriptor = Object.getOwnPropertyDescriptor(parts, mastersXmlKey);
        const mastersXml = Object.prototype.hasOwnProperty.call(parts, mastersXmlKey)
            ? (mastersXmlDescriptor ? mastersXmlDescriptor.value : undefined)
            : undefined;
        if (mastersXml) {
            // mastersXml may be wrapped: { Masters: { Master: [...] } }
            const mastersNode = hasKey(mastersXml, 'Masters') ? mastersXml['Masters'] : mastersXml;
            const mastersArray = ensureArray(mastersNode['Master']);
            for (const masterObj of mastersArray) {
                if (!masterObj || !masterObj.$) {
                    continue;
                }
                const attrs = masterObj.$;
                const attrID = attrs['ID'];
                const masterId = (attrID != null ? String(attrID) : '');
                if (!masterId) {
                    continue;
                }
                let rid = '';
                const masterRel = masterObj['Rel'];
                if (masterRel && masterRel['$']) {
                    const relAttrs = masterRel['$'];
                    const relAttrsRecord = relAttrs;
                    rid = (typeof relAttrsRecord['r:id'] === 'string') ? relAttrsRecord['r:id'] :
                        (typeof relAttrsRecord['rId'] === 'string') ? relAttrsRecord['rId'] : '';
                }
                const attrNameU = attrs['NameU'];
                const attrName = attrs['Name'];
                const nameU = (attrNameU != null ? String(attrNameU) :
                    (attrName != null ? String(attrName) : ''));
                const fileName = (rid && indexObj.masterFileByRid.has(rid)) ? (indexObj.masterFileByRid.get(rid) || '') : '';
                indexObj.mastersById.set(masterId, { id: masterId, nameU: nameU, rid: rid, fileName: fileName });
            }
        }
        else {
            context.addWarning('[WARNING] :: masters.xml is missing; master index may be empty.');
        }
        // ---- 3) Build masterChildByMasterId: index all descendants by $.ID per master file ----
        indexObj.mastersById.forEach((masterInfo) => {
            const file = masterInfo && masterInfo.fileName ? String(masterInfo.fileName) : '';
            if (!file) {
                return;
            }
            // Safe property access
            const masterJsDescriptor = Object.getOwnPropertyDescriptor(parts, file);
            if (!masterJsDescriptor || !masterJsDescriptor.value) {
                return;
            }
            const masterJs = masterJsDescriptor.value;
            // Some parsers: { Master: {... MasterContents ...} }, others: root is <Master>
            const rootMaster = hasKey(masterJs, 'Master') ? masterJs['Master'] : masterJs;
            let shapesRoot = null;
            // 1) <Master><MasterContents><Shapes>...</Shapes></MasterContents>
            if (hasKey(rootMaster, 'MasterContents')) {
                const masterContent = rootMaster['MasterContents'];
                if (masterContent && hasKey(masterContent, 'Shapes')) {
                    shapesRoot = masterContent['Shapes'];
                }
            }
            // 2) <Master><Shapes>...</Shapes></Master>
            if (!shapesRoot && hasKey(rootMaster, 'Shapes')) {
                shapesRoot = rootMaster['Shapes'];
            }
            // 3) Flattened layout at file root: { $, Shapes }  (this matches your screenshot)
            if (!shapesRoot && hasKey(masterJs, 'Shapes')) {
                shapesRoot = masterJs['Shapes'];
            }
            const childMap = new Map();
            const parentMap = new Map(); // childId -> parentId
            const childrenMap = new Map(); // parentId -> [childId]
            const rootIds = []; // shapes with no parent
            if (shapesRoot && hasKey(shapesRoot, 'Shape')) {
                const topChildren = ensureArray(shapesRoot['Shape']);
                // local helper: add child to parent’s list
                const addChild = (parentId, childId) => {
                    let arr = childrenMap.get(parentId);
                    if (!arr) {
                        arr = [];
                        childrenMap.set(parentId, arr);
                    }
                    if (arr.indexOf(childId) === -1) {
                        arr.push(childId);
                    }
                };
                // NOTE: pass parentId for hierarchy; parentId === '' means "no parent" (root)
                const collectById = (node, parentId) => {
                    if (!node || !node.$) {
                        return;
                    }
                    const nodeAttrs = node.$;
                    const nodeID = nodeAttrs['ID'];
                    const idStr = (nodeID != null) ? String(nodeID) : '';
                    if (!idStr) {
                        return;
                    }
                    // 1) Flat lookup
                    childMap.set(idStr, node);
                    // 2) Hierarchy
                    if (parentId && parentId.length > 0) {
                        parentMap.set(idStr, parentId);
                        addChild(parentId, idStr);
                    }
                    else {
                        if (rootIds.indexOf(idStr) === -1) {
                            rootIds.push(idStr);
                        }
                    }
                    // 3) Recurse into nested Shapes
                    const nodeShapes = node['Shapes'];
                    const nodeShapesShape = nodeShapes && nodeShapes['Shape'];
                    const nested = nodeShapesShape ? ensureArray(nodeShapesShape) : [];
                    for (let i = 0; i < nested.length; i++) {
                        collectById(nested[parseInt(i.toString(), 10)], idStr);
                    }
                };
                // kick off: top-level shapes have no parent
                for (let c = 0; c < topChildren.length; c++) {
                    collectById(topChildren[parseInt(c.toString(), 10)], '');
                }
            }
            // Save all maps for this master
            const masterIdStr = String(masterInfo.id);
            indexObj.masterChildByMasterId.set(masterIdStr, childMap);
            indexObj.masterParentByMasterId.set(masterIdStr, parentMap);
            indexObj.masterImmediateChildrenByMasterId.set(masterIdStr, childrenMap);
            indexObj.masterRootIdsByMasterId.set(masterIdStr, rootIds);
        });
        // ---- 4) Per-page Connects by file (optional) ----
        const selectedPageFile = doc.__SelectedPageFile;
        if (typeof selectedPageFile === 'string' && Object.prototype.hasOwnProperty.call(parts, selectedPageFile)) {
            const pageJsDescriptor = Object.getOwnPropertyDescriptor(parts, selectedPageFile);
            const pageJs = pageJsDescriptor ? pageJsDescriptor.value : undefined;
            if (pageJs && pageJs.Connects) {
                indexObj.pageConnectsByFile.set(selectedPageFile, pageJs.Connects);
            }
        }
    }
}
